import type { ReactNode } from 'react';
import type {
  IUserSettingsCmsTerms,
  IUserSettingsPredefinedField,
  IUserSettingsSubmittalUdf,
} from 'app/services/userSettingsApi';
import { appPreviewImageSrcs } from './appPreviewImages';
import { submittalCmsBody, type SubmittalCmsDocument } from './submittalModel';

/**
 * Column 3 of the Submittal tab — `submittal.tmpl:147-313`,
 * `#submmital-fields-data-wrapper > #myTabContent` (the misspelling is legacy's;
 * keep it, `styles.css` selects on it).
 *
 * One `.tab-pane` per library row, all three sources sharing one tab-content
 * container, exactly one of them `.active`.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ★★ Q1 — THE USER PANES ARE RENDERED **EMPTY**, AND FILL ONLY ON HOVER. ★★
 *
 * This is the single most conspicuous behaviour on the page and it is a
 * REQUIREMENT, not a defect to fix.
 *
 * `submittal.tmpl:152-155` emits, for each of the 283 user fields, a `tab-pane`
 * with NO CHILDREN — just `id`, `data-value` and the aria wiring. The body
 * (title input, "Start from new page" checkbox, and either the contenteditable
 * or the PDF iframe) is injected later by `loadPdf` (`submittal.js:599-618`),
 * which is bound to **`mouseover`** on the library anchor:
 *
 *     'mouseover .submittal-row .user-fields-block.nav-tabs a': 'loadPdf'
 *
 * and which itself only acts `if (field_row && !field_row.html())` — i.e. once
 * per pane, whichever way it is reached.
 *
 * `initialize` (`:148`) then does
 *   `…li.each-element-row:nth-child(1) a.submittal-item-for-drag').click()`
 * — a **click**, which fires Bootstrap's tab plugin (so pane #1 becomes
 * `.active in` and is the visible one) but does NOT fire `mouseover`. So on
 * first paint the right-hand 2/5 of the Submittal tab is a blank white
 * rectangle, and it stays blank until the pointer crosses the first library
 * row. Verified in discovery: pane innerHTML `0` → `2039` after one hover.
 *
 * Reproduced HERE by `filledIds`: a pane whose id is not in the set renders as
 * the bare `<div/>` the template emits. `SubmittalTab` adds an id to the set in
 * exactly the places legacy calls `loadPdf` — the anchor's `mouseover`, the
 * format rail's `startLoadPdf` mouseover, `active_last_change` after a content
 * save, and the create flow's 10 ms timer — and CLEARS the whole set on every
 * full `render()`, because `$el.html()` throws every filled pane away. (Yes:
 * creating a UDF blanks every pane you had already hovered. That is legacy.)
 *
 * ★ The APPLICATION and CMS panes are NOT lazy — the template renders them in
 * full. Only the User panes are empty. (The Proposal tab is not lazy at all;
 * that asymmetry is U-e's problem.)
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * ★ BOOTSTRAP FADE SEMANTICS. `Tab.prototype.activate` adds `.active`, then —
 * because the pane carries `.fade` — adds `.in` after a forced reflow. When the
 * pane is later deactivated only `.active` is removed; the `.in` STAYS. So the
 * fade-in animation plays once per pane per render and never again. `panesWithIn`
 * is that sticky half.
 */

interface FieldDetailProps {
  /** The render snapshot's user rows (same list the library `<ul>` renders). */
  userFields: IUserSettingsSubmittalUdf[];
  appFields: IUserSettingsPredefinedField[];
  cmsDocuments: SubmittalCmsDocument[];
  terms: IUserSettingsCmsTerms | undefined;
  /** The one pane with `.active`, or `null` (nothing selected). */
  activePaneId: string | null;
  /** Panes that have ever been activated — they keep Bootstrap's `.in`. */
  panesWithIn: ReadonlySet<string>;
  /** ★ Q1 — user field ids whose pane `loadPdf` has filled. */
  filledIds: ReadonlySet<number>;
  /** U-c's `user.defind.row.tmpl` body for one filled user pane. */
  renderUserPane: (field: IUserSettingsSubmittalUdf) => ReactNode;
  /** `keyup`/`change .textarea-field-title` → `checkField` (`:417-426`). Bound
   * on the CMS panes' readonly title inputs too, exactly as legacy does. */
  onTitleCheck: (docValue: string, input: HTMLInputElement) => void;
  /** Which readonly CMS title inputs currently carry `.invalid-field`. */
  invalidTitles: ReadonlySet<string>;
}

const FieldDetail = ({
  userFields,
  appFields,
  cmsDocuments,
  terms,
  activePaneId,
  panesWithIn,
  filledIds,
  renderUserPane,
  onTitleCheck,
  invalidTitles,
}: FieldDetailProps) => {
  const paneState = (id: string) =>
    `${activePaneId === id ? ' active' : ''}${panesWithIn.has(id) ? ' in' : ''}`;

  return (
    <div
      className="col-xs-12 col-md-6 col-lg-8 left-column-scrollable app-field"
      id="submmital-fields-data-wrapper"
    >
      <div className="row full-height">
        <div className="col-xs-12 full-height">
          <div className="col-xs-12 full-height tab-content pl-0" id="myTabContent">
            {/* ── USER — ★ Q1: EMPTY until hovered ─────────────────────── */}
            {userFields.map((field) => {
              const id = `submittal_user_defined_field_${field.id}`;
              return (
                <div
                  key={field.id}
                  className={`col-xs-12 tab-pane fade submittal_user_defined_field_${field.id}${paneState(
                    id
                  )}`}
                  id={id}
                  role="tabpanel"
                  aria-labelledby={`${id}-tab`}
                  data-value={field.id}
                >
                  {filledIds.has(field.id) ? renderUserPane(field) : null}
                </div>
              );
            })}

            {/* ── APPLICATION — static preview images, never lazy ───────── */}
            {appFields.map((field) => {
              const id = `submittal_${field.value}`;
              const srcs = appPreviewImageSrcs(field.value);
              return (
                <div
                  /* ★ Q14 — two master rows share the "Center of Gravity Calc"
                     value, so two panes share this id. `field.id` keeps React's
                     key unique where the DOM id is not. */
                  key={field.id}
                  className={`col-xs-12 tab-pane fade content-image${paneState(id)}`}
                  id={id}
                >
                  <div className="col-xs-12">
                    <div className="col-xs-12 content-title non-editable">
                      <span>{field.name}</span>{' '}
                    </div>
                  </div>
                  <div className="col-xs-12 content-text scrollable">
                    <div className={`col-xs-12 ${field.value}`}>
                      {srcs.length > 0 && (
                        <div>
                          {srcs.map((src) => (
                            <img key={src} src={src} alt="" />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* ── CMS — read-only, body injected as raw HTML ────────────── */}
            {cmsDocuments.map((doc) => {
              const id = `submittal_cms_${doc.key}`;
              return (
                <div
                  key={doc.value}
                  className={`col-xs-12 tab-pane fade submittal_cms_${doc.key}${paneState(id)}`}
                  id={id}
                  role="tabpanel"
                  aria-labelledby={`${id}-tab`}
                >
                  <div className="col-xs-12 content-header">
                    <div className="col-xs-6  non-editable">
                      {/* `readonly="true"` in the template — this is the whole
                          reason the CMS terms are unwritable from this page
                          (discovery §6 "Write paths that DO NOT exist"). */}
                      <input
                        type="text"
                        name="name"
                        className={`textarea-field-title${
                          invalidTitles.has(doc.value) ? ' invalid-field' : ''
                        }`}
                        defaultValue={doc.label}
                        readOnly
                        onKeyUp={(e) => onTitleCheck(doc.value, e.currentTarget)}
                        onChange={(e) => onTitleCheck(doc.value, e.currentTarget)}
                      />
                    </div>
                    <div className="col-xs-6">
                      <div className="col-xs-8" />
                      <div className="col-xs-1" />
                    </div>
                  </div>
                  <div className="col-xs-12 content-text ">
                    <div className="col-xs-12">
                      {/* `render()` (`:183-187`) `.append()`s the terms HTML into
                          this div. The payload is server-authored CMS
                          boilerplate rendered as markup on the legacy page, so
                          it is rendered as markup here. */}
                      <div
                        className={`textarea-field col-xs-12  ${doc.value}_textarea`}
                        dangerouslySetInnerHTML={{ __html: submittalCmsBody(terms, doc.value) }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FieldDetail;
